// src/components/OrgChart.jsx
// Vista de organigrama estilo "Buk": tarjetas con avatar + nombre + cargo,
// conectadas por lineas, con un badge "N v" bajo cada persona para
// expandir/colapsar a sus reportes directos. Se arma a partir del mismo
// arbol de areas/cargos que ya devuelve GET /areas/tree -- no es un dato
// nuevo, es una segunda forma de leer la misma jerarquia: cada area con
// jefatura se convierte en la "persona" que agrupa como reportes directos
// a los demas cargos de esa area y a la jefatura de cada sub-area.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { ChevronDown, Users } from './icons';

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500',
  'bg-violet-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-pink-500',
];

function colorFor(nombre) {
  let hash = 0;
  for (let i = 0; i < (nombre || '').length; i++) hash = nombre.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// El nombre y apellido que devuelve GET /areas/tree vienen de AuthUser
// (la cuenta de acceso), que puede quedar desactualizado frente a la
// nomina real -- por eso, si hay coincidencia por email en el mapa de
// nomina, se usa ese nombre en vez del guardado en el cargo.
function leafNode(cargoUsuario, area, usersByEmail) {
  const u = cargoUsuario.usuario;
  const nomina = u?.email ? usersByEmail?.get(u.email.toLowerCase()) : null;
  const nombre = nomina
    ? `${nomina.nombre || ''} ${nomina.apellido || ''}`.trim()
    : u ? `${u.nombre || ''} ${u.apellido || ''}`.trim() : '(sin asignar)';

  return {
    id: `cargo-${cargoUsuario.id}`,
    nombre: nombre || '(sin nombre)',
    cargo: cargoUsuario.cargo,
    areaNombre: area.nombre,
    email: u?.email || null,
    nominaId: nomina?.id ?? null,
    esJefatura: cargoUsuario.esJefatura,
    // Referencias crudas, para poder editar el cargo desde la tarjeta.
    cargoUsuario,
    area,
    hijos: [],
  };
}

// Transforma el arbol de areas (area -> cargos + sub-areas) en un arbol de
// personas: la jefatura de un area es el nodo, y sus "hijos" son el resto
// de cargos de esa misma area mas la jefatura de cada sub-area directa.
function buildPersonForest(areas, usersByEmail) {
  const result = [];
  for (const area of areas || []) {
    const cargos = area.cargos || [];
    const jefes = cargos.filter((c) => c.esJefatura && c.usuario);
    const noJefes = cargos.filter((c) => !c.esJefatura && c.usuario);
    const hijosDeSubareas = buildPersonForest(area.children, usersByEmail);

    if (jefes.length === 0) {
      // Sin jefatura definida en esta area: sus cargos y las jefaturas de
      // las sub-areas quedan como raices independientes (no hay a quien
      // colgarlos), en vez de perderse silenciosamente.
      result.push(...noJefes.map((c) => leafNode(c, area, usersByEmail)));
      result.push(...hijosDeSubareas);
      continue;
    }

    for (const jefe of jefes) {
      result.push({
        ...leafNode(jefe, area, usersByEmail),
        hijos: [...noJefes.map((c) => leafNode(c, area, usersByEmail)), ...hijosDeSubareas],
      });
    }
  }
  return result;
}

function NodeCard({ node, expanded, onToggle, onOpenDetail, onEdit }) {
  const initials = node.nombre
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '?';

  return (
    <div className="flex flex-col items-center w-32 sm:w-36 shrink-0">
      <div
        onClick={onOpenDetail}
        role={onOpenDetail ? 'button' : undefined}
        title={onOpenDetail ? 'Ver ficha del usuario en Nómina' : undefined}
        className={`group/card relative w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-850 shadow-sm p-2.5 flex flex-col items-center text-center transition-all ${onOpenDetail ? 'cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md' : ''
          }`}
      >
        {onEdit && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            title="Editar cargo"
            className="absolute top-1 right-1 p-1 rounded-md text-gray-300 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 opacity-0 group-hover/card:opacity-100 transition-opacity"
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
        <div className={`w-11 h-11 rounded-full ${colorFor(node.nombre)} text-white flex items-center justify-center font-bold text-sm shrink-0`}>
          {initials}
        </div>
        <p className="mt-2 text-xs font-semibold text-gray-900 dark:text-white leading-tight line-clamp-2">
          {node.nombre}
        </p>
        <p className="mt-0.5 text-[11px] font-medium text-blue-600 dark:text-blue-400 leading-tight line-clamp-2">
          {node.cargo}
        </p>
        {node.areaNombre && (
          <p className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500 truncate w-full">{node.areaNombre}</p>
        )}
      </div>
      {node.hijos.length > 0 && (
        <button
          type="button"
          onClick={onToggle}
          className="mt-1.5 flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          {node.hijos.length}
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
}

function OrgChartNode({ node, defaultExpanded = false, onEditCargo }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const navigate = useNavigate();
  const hasHijos = node.hijos.length > 0;

  return (
    <li>
      <NodeCard
        node={node}
        expanded={expanded}
        onToggle={() => setExpanded((e) => !e)}
        onOpenDetail={node.nominaId ? () => navigate(`/usuarios/${node.nominaId}`) : undefined}
        onEdit={onEditCargo ? () => onEditCargo(node.area, node.cargoUsuario) : undefined}
      />
      {hasHijos && expanded && (
        <ul>
          {node.hijos.map((child) => (
            <OrgChartNode key={child.id} node={child} onEditCargo={onEditCargo} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function OrgChart({ tree, allUsers, onEditCargo }) {
  const usersByEmail = useMemo(() => {
    const map = new Map();
    for (const u of allUsers || []) {
      if (u.email) map.set(u.email.toLowerCase(), u);
    }
    return map;
  }, [allUsers]);

  const forest = useMemo(() => buildPersonForest(tree, usersByEmail), [tree, usersByEmail]);

  if (forest.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Users className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Aún no hay jefaturas definidas para armar el organigrama visual.
        </p>
      </div>
    );
  }

  return (
    <div className="hd-orgchart overflow-x-auto pb-4">
      <style>{`
        .hd-orgchart ul { display: flex; padding-top: 28px; position: relative; }
        .hd-orgchart ul.hd-orgchart-root { padding-top: 0; }
        .hd-orgchart li { display: flex; flex-direction: column; align-items: center; list-style: none; position: relative; padding: 28px 10px 0 10px; }
        .hd-orgchart li::before, .hd-orgchart li::after { content: ''; position: absolute; top: 0; right: 50%; width: 50%; height: 28px; border-top: 2px solid #d1d5db; }
        .hd-orgchart li::after { right: auto; left: 50%; border-left: 2px solid #d1d5db; }
        .hd-orgchart li:only-child::before, .hd-orgchart li:only-child::after { display: none; }
        .hd-orgchart li:first-child::before { border: none; }
        .hd-orgchart li:last-child::after { border: none; }
        .hd-orgchart li:last-child::before { border-right: 2px solid #d1d5db; border-radius: 0 6px 0 0; }
        .hd-orgchart li:first-child::after { border-radius: 6px 0 0 0; }
        .hd-orgchart li > ul::before { content: ''; position: absolute; top: 0; left: 50%; border-left: 2px solid #d1d5db; width: 0; height: 28px; }
        .dark .hd-orgchart li::before, .dark .hd-orgchart li::after, .dark .hd-orgchart li > ul::before { border-color: #4b5563; }
      `}</style>
      {/* Cada raiz es su propio <ul> independiente (li:only-child esconde su
          propia linea de conexion): asi no hace falta un padre en comun
          cuando hay mas de un area de nivel superior. */}
      <div className="flex justify-center gap-10 flex-wrap min-w-full w-max mx-auto">
        {forest.map((root) => (
          <ul key={root.id} className="hd-orgchart-root">
            <OrgChartNode node={root} defaultExpanded onEditCargo={onEditCargo} />
          </ul>
        ))}
      </div>
    </div>
  );
}
